import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Phone, Shield, Clock, Award, ArrowRight, Star, ChevronLeft, ChevronRight, Check } from "lucide-react";
import axios from "axios";
import SEO, { LOCATION_KEYWORDS } from "@/components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HomePage = () => {
  const [featuredProjects, setFeaturedProjects] = useState([]);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  const testimonials = [
    {
      name: "Sandy Williams",
      location: "Blantyre",
      text: "We'd shopped around for a while but decided to go with C.Glancey Double Glazing. I'm so glad we did! From start to finish the process was great.",
    },
    {
      name: "Quinn Davis",
      location: "Glasgow",
      text: "Excellent sales service from Charles and the team. Great communication ahead of install and super helpful and efficient installation from the guys. Happy to recommend Value Double Glazing to others.",
    },
    {
      name: "Casey John",
      location: "Newton Mearns",
      text: "I was always kept informed of what was happening and when the windows were being fitted. The fitters were polite and friendly and even had a hoover round when they'd finished. Would definitely recommend.",
    },
    {
      name: "Margaret Thomson",
      location: "Motherwell",
      text: "Absolutely delighted with our new composite door and windows. The quality is fantastic and the price was very competitive. Charles was professional throughout and the installers were tidy and efficient.",
    },
    {
      name: "James & Linda McPherson",
      location: "Hamilton",
      text: "Had our whole house done with new double glazing. What a difference it's made to our heating bills! The team were brilliant from quote to completion. Highly recommend C.Glancey to anyone in Lanarkshire.",
    },
    {
      name: "Robert Anderson",
      location: "Wishaw",
      text: "Fantastic service from start to finish. Our new garden room is exactly what we wanted - perfect for working from home. Great attention to detail and excellent value for money. Thank you!",
    },
    {
      name: "Karen Stewart",
      location: "Bellshill",
      text: "Our new front door has completely transformed the look of our house. The anthracite grey composite door looks stunning and feels so secure. Excellent workmanship from the C.Glancey team.",
    },
    {
      name: "David & Susan Murray",
      location: "Airdrie",
      text: "We replaced all our windows and couldn't be happier. The house is so much warmer and quieter now. Professional service, fair pricing, and the installers left everything spotless. 5 stars!",
    },
    {
      name: "Paul Henderson",
      location: "Coatbridge",
      text: "Had a bay window fitted and it looks absolutely brilliant. Charles gave great advice on styles and the fitting team were fantastic. No hesitation in recommending C.Glancey Double Glazing.",
    },
    {
      name: "Anne Marie Kelly",
      location: "East Kilbride",
      text: "From the initial quote to the final installation, everything was handled professionally. Our new patio doors have opened up our living space beautifully. Great value and top quality work.",
    },
  ];

  const fetchFeaturedProjects = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/projects?featured=true`);
      setFeaturedProjects(response.data.slice(0, 4));
    } catch {
      // Silently handle error - projects will remain empty
    }
  }, []);

  useEffect(() => {
    fetchFeaturedProjects();
  }, [fetchFeaturedProjects]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [testimonials.length]);

  const services = [
    {
      title: "Double Glazing",
      description: "Energy-efficient A-rated windows to reduce heat loss and lower energy bills.",
      image: "https://static.wixstatic.com/media/4dd2ec_0e077c66652d4d22b5140327c134eaf1~mv2.jpg/v1/fill/w_605,h_992,al_c,q_85,enc_avif,quality_auto/4dd2ec_0e077c66652d4d22b5140327c134eaf1~mv2.jpg",
      link: "/windows",
    },
    {
      title: "Composite Doors & uPVC Doors",
      description: "Secure, stylish composite and uPVC doors in a range of beautiful colours.",
      image: "https://static.wixstatic.com/media/4dd2ec_d37f7d12c7ee44b49c9895a734f537a0~mv2.jpg/v1/fill/w_609,h_922,al_c,q_85,enc_avif,quality_auto/4dd2ec_d37f7d12c7ee44b49c9895a734f537a0~mv2.jpg",
      link: "/doors",
    },
    {
      title: "Garden Rooms",
      description: "Beautiful outdoor buildings perfect for offices, gyms, or entertainment spaces.",
      image: "https://static.wixstatic.com/media/4dd2ec_3c902dbbd7cc427fa8b63e00a4494584~mv2.jpg/v1/fill/w_700,h_1000,al_c,q_80,enc_avif,quality_auto/4dd2ec_3c902dbbd7cc427fa8b63e00a4494584~mv2.jpg",
      link: "/outbuilds",
    },
  ];

  const features = [
    {
      icon: Shield,
      title: "10 Year Guarantee",
      description: "All our products backed by a comprehensive 10 year warranty.",
    },
    {
      icon: Clock,
      title: "Fast Response",
      description: "Quick response service for all your glazing needs.",
    },
    {
      icon: Award,
      title: "Quality Materials",
      description: "Using only the best Rehau & Veka products on the market.",
    },
  ];

  return (
    <div data-testid="home-page" className="pt-32 lg:pt-36">
      <SEO 
        title="Double Glazing Motherwell, Bellshill, Hamilton, Wishaw, Airdrie | C. Glancey"
        description="A-rated double glazing, uPVC windows, composite doors & garden rooms in Motherwell, Bellshill, Hamilton, Wishaw & Airdrie. Professional installation, 10 year guarantee. Free quotes - call 07917 181597"
        keywords={`double glazing, double glazing Motherwell, double glazing Bellshill, double glazing Hamilton, double glazing Wishaw, double glazing Airdrie, double glazing Lanarkshire, uPVC windows, A-rated windows, composite doors, bay windows, French doors, patio doors, garden rooms, window installation, door installation, ${LOCATION_KEYWORDS}`}
        canonical="/"
      />
      {/* Hero Section */}
      <section
        data-testid="hero-section"
        className="relative min-h-[90vh] flex items-center"
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://static.wixstatic.com/media/4dd2ec_b2e9065811fd4c12b1272bbab94f808a~mv2.jpg/v1/fill/w_1536,h_645,al_c,q_85,enc_avif,quality_auto/4dd2ec_b2e9065811fd4c12b1272bbab94f808a~mv2.jpg')`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A]/95 via-[#0A0A0A]/70 to-transparent" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="max-w-2xl">
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-black text-white leading-tight mb-6 fade-up"
              style={{ fontFamily: "'Chivo', sans-serif" }}
            >
              DOUBLE GLAZING{" "}
              <span className="text-[#FF6B00]">MOTHERWELL, BELLSHILL, HAMILTON, WISHAW & AIRDRIE</span>
              <span className="block text-2xl sm:text-3xl mt-2 text-white">| C. Glancey</span>
            </h1>
            <p className="text-lg text-gray-300 mb-8 fade-up stagger-1">
              Upgrade your home with premium double glazing from C.Glancey
              Windows and Doors in Lanarkshire. Energy-efficient <Link to="/windows" className="text-[#FF6B00] hover:underline">uPVC windows</Link> and <Link to="/doors" className="text-[#FF6B00] hover:underline">composite doors</Link> that help
              reduce heat loss, lower energy bills, and improve sound insulation. We also build stunning <Link to="/outbuilds" className="text-[#FF6B00] hover:underline">garden rooms</Link>.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 fade-up stagger-2">
              <Link
                to="/contact"
                data-testid="hero-cta-quote"
                className="bg-[#FF6B00] text-white font-bold uppercase tracking-wide px-8 py-4 hover:bg-[#E65000] transition-all hover:translate-x-1 text-center"
              >
                Get Free Quote
              </Link>
              <a
                href="tel:07917181597"
                data-testid="hero-cta-call"
                className="bg-transparent border-2 border-white text-white font-bold uppercase tracking-wide px-8 py-4 hover:bg-white hover:text-[#0A0A0A] transition-all flex items-center justify-center gap-2"
              >
                <Phone className="w-5 h-5" />
                07917 181597
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Features Bar */}
      <section className="bg-[#0A0A0A] py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                data-testid={`feature-${index}`}
                className="flex items-center space-x-4"
              >
                <div className="w-14 h-14 bg-[#FF6B00] flex items-center justify-center flex-shrink-0">
                  <feature.icon className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h3 className="text-white font-bold uppercase tracking-wide">
                    {feature.title}
                  </h3>
                  <p className="text-gray-400 text-sm">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section data-testid="services-section" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2
              className="text-3xl sm:text-4xl font-black text-[#0A0A0A] mb-4"
              style={{ fontFamily: "'Chivo', sans-serif" }}
            >
              OUR <span className="text-[#FF6B00]">SERVICES</span>
            </h2>
            <p className="text-[#525252] max-w-2xl mx-auto">
              From single <Link to="/windows" className="text-[#FF6B00] hover:underline">windows</Link> to a complete home transformation with new <Link to="/doors" className="text-[#FF6B00] hover:underline">doors</Link> and <Link to="/outbuilds" className="text-[#FF6B00] hover:underline">garden rooms</Link>, we can tailor
              a package to suit every budget.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service) => (
              <Link
                key={service.title}
                to={service.link}
                data-testid={`service-card-${service.title.toLowerCase().replace(/\s+/g, '-')}`}
                className="group service-card bg-white border border-[#E5E5E5] overflow-hidden hover:border-[#FF6B00] transition-all"
              >
                <div className="aspect-[3/4] overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="p-6">
                  <h3
                    className="text-xl font-black text-[#0A0A0A] mb-2"
                    style={{ fontFamily: "'Chivo', sans-serif" }}
                  >
                    {service.title}
                  </h3>
                  <p className="text-[#525252] text-sm mb-4">{service.description}</p>
                  <span className="text-[#FF6B00] font-bold uppercase text-sm tracking-wide flex items-center gap-2 group-hover:gap-3 transition-all">
                    Learn More <ArrowRight className="w-4 h-4" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Recent Work Section */}
      {featuredProjects.length > 0 && (
        <section data-testid="recent-work-section" className="py-24 bg-[#F5F5F5]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-12">
              <div>
                <h2
                  className="text-3xl sm:text-4xl font-black text-[#0A0A0A] mb-2"
                  style={{ fontFamily: "'Chivo', sans-serif" }}
                >
                  RECENT <span className="text-[#FF6B00]">WORK</span>
                </h2>
                <p className="text-[#525252]">
                  Some of our recent supply and fit work through Lanarkshire
                </p>
              </div>
              <Link
                to="/recent-work"
                data-testid="view-all-work"
                className="mt-4 sm:mt-0 text-[#FF6B00] font-bold uppercase text-sm tracking-wide flex items-center gap-2 hover:gap-3 transition-all"
              >
                View All Work <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {featuredProjects.map((project) => (
                <div
                  key={project.id}
                  data-testid={`featured-project-${project.id}`}
                  className="group bg-white border border-[#E5E5E5] overflow-hidden hover:border-[#FF6B00] transition-all"
                >
                  <div className="aspect-[3/4] overflow-hidden">
                    <img
                      src={project.image_url}
                      alt={project.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-4">
                    <span className="text-xs uppercase tracking-wide text-[#FF6B00] font-bold">
                      {project.category}
                    </span>
                    <h3 className="font-bold text-[#0A0A0A] mt-1">{project.title}</h3>
                    {project.location && (
                      <p className="text-sm text-[#525252]">{project.location}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Testimonials Section */}
      <section data-testid="testimonials-section" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2
              className="text-3xl sm:text-4xl font-black text-[#0A0A0A] mb-4"
              style={{ fontFamily: "'Chivo', sans-serif" }}
            >
              WHAT OUR <span className="text-[#FF6B00]">CLIENTS SAY</span>
            </h2>
          </div>

          <div className="relative max-w-3xl mx-auto">
            <div className="bg-[#F5F5F5] p-8 md:p-12 relative">
              <div className="absolute -top-6 left-8 text-8xl text-[#FF6B00] opacity-20 font-serif">
                "
              </div>
              <div className="flex mb-4">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star
                    key={star}
                    className="w-5 h-5 text-[#FF6B00] fill-[#FF6B00]"
                  />
                ))}
              </div>
              <p className="text-lg text-[#0A0A0A] mb-6 italic">
                "{testimonials[currentTestimonial].text}"
              </p>
              <div>
                <p className="font-bold text-[#0A0A0A]">
                  {testimonials[currentTestimonial].name}
                </p>
                <p className="text-sm text-[#525252]">
                  {testimonials[currentTestimonial].location}
                </p>
              </div>
            </div>

            <div className="flex justify-center mt-6 gap-4">
              <button
                data-testid="testimonial-prev"
                onClick={() =>
                  setCurrentTestimonial(
                    (prev) => (prev - 1 + testimonials.length) % testimonials.length
                  )
                }
                className="w-10 h-10 border border-[#E5E5E5] flex items-center justify-center hover:border-[#FF6B00] hover:text-[#FF6B00] transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-2">
                {testimonials.map((_, idx) => (
                  <button
                    key={`testimonial-dot-${idx}`}
                    data-testid={`testimonial-dot-${idx}`}
                    onClick={() => setCurrentTestimonial(idx)}
                    className={`w-2 h-2 transition-colors ${
                      currentTestimonial === idx
                        ? "bg-[#FF6B00]"
                        : "bg-[#E5E5E5]"
                    }`}
                  />
                ))}
              </div>
              <button
                data-testid="testimonial-next"
                onClick={() =>
                  setCurrentTestimonial((prev) => (prev + 1) % testimonials.length)
                }
                className="w-10 h-10 border border-[#E5E5E5] flex items-center justify-center hover:border-[#FF6B00] hover:text-[#FF6B00] transition-colors"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
            
            {/* Google Business Profile Link */}
            <div className="text-center mt-8">
              <a
                href="https://share.google/XwiDGekGfETNkuibI"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="google-business-link"
                className="inline-flex items-center gap-3 bg-white border-2 border-[#E5E5E5] hover:border-[#FF6B00] px-6 py-3 rounded-lg transition-all hover:shadow-lg group"
              >
                <svg className="w-6 h-6" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                <span className="font-semibold text-[#0A0A0A] group-hover:text-[#FF6B00] transition-colors">
                  See More Reviews on Google
                </span>
                <ArrowRight className="w-4 h-4 text-[#525252] group-hover:text-[#FF6B00] group-hover:translate-x-1 transition-all" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section data-testid="cta-section" className="bg-[#FF6B00] py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2
            className="text-3xl sm:text-4xl font-black text-white mb-4"
            style={{ fontFamily: "'Chivo', sans-serif" }}
          >
            READY TO UPGRADE YOUR HOME?
          </h2>
          <p className="text-white/90 mb-8 max-w-2xl mx-auto">
            Get in touch today for a free, no-obligation quote on premium <Link to="/windows" className="text-white underline hover:text-[#0A0A0A]">double glazing</Link>, <Link to="/doors" className="text-white underline hover:text-[#0A0A0A]">doors</Link>, or <Link to="/outbuilds" className="text-white underline hover:text-[#0A0A0A]">garden rooms</Link>. We cover Lanarkshire,
            Glasgow, and all surrounding areas. Read our <Link to="/reviews" className="text-white underline hover:text-[#0A0A0A]">customer reviews</Link>.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              data-testid="cta-quote"
              className="bg-white text-[#0A0A0A] font-bold uppercase tracking-wide px-8 py-4 hover:bg-[#0A0A0A] hover:text-white transition-all"
            >
              Get Free Quote
            </Link>
            <a
              href="tel:07917181597"
              data-testid="cta-call"
              className="bg-transparent border-2 border-white text-white font-bold uppercase tracking-wide px-8 py-4 hover:bg-white hover:text-[#0A0A0A] transition-all flex items-center justify-center gap-2"
            >
              <Phone className="w-5 h-5" />
              Call Now
            </a>
          </div>
        </div>
      </section>

      {/* Brands Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-[#525252] uppercase tracking-wide text-sm mb-8">
            We use only premium quality materials for our double glazing in Lanarkshire
          </p>
          <div className="flex flex-wrap justify-center items-center gap-12">
            <img
              src="https://static.wixstatic.com/media/4dd2ec_5b4807bcd391487898654e93c65ee4f2~mv2.png/v1/fill/w_243,h_135,al_c,q_85,enc_avif,quality_auto/rehau-logo-vector-removebg-preview.png"
              alt="Rehau"
              className="h-12 object-contain opacity-60 hover:opacity-100 transition-opacity"
            />
            <img
              src="https://static.wixstatic.com/media/4dd2ec_024373e2150141c49aebc24a8ffa6c30~mv2.png/v1/fill/w_312,h_128,al_c,q_85,enc_avif,quality_auto/veka-logo-1-removebg-preview.png"
              alt="Veka"
              className="h-12 object-contain opacity-60 hover:opacity-100 transition-opacity"
            />
            <img
              src="https://static.wixstatic.com/media/4dd2ec_d4bf52770c1543038084656ffe147d67~mv2.png/v1/fill/w_304,h_166,al_c,q_85,enc_avif,quality_auto/download-removebg-preview.png"
              alt="Yale"
              className="h-12 object-contain opacity-60 hover:opacity-100 transition-opacity"
            />
          </div>
        </div>
      </section>

      {/* Internal Links Section for SEO */}
      <section className="py-16 bg-[#F5F5F5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2
            className="text-2xl sm:text-3xl font-black text-[#0A0A0A] mb-8 text-center"
            style={{ fontFamily: "'Chivo', sans-serif" }}
          >
            EXPLORE OUR <span className="text-[#FF6B00]">SERVICES</span>
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <Link to="/windows" className="bg-white p-6 text-center hover:shadow-lg transition-shadow border border-[#E5E5E5] hover:border-[#FF6B00]">
              <h3 className="font-bold text-[#0A0A0A] mb-2">Double Glazing Windows</h3>
              <p className="text-sm text-[#525252]">uPVC & A-rated windows for your home</p>
            </Link>
            <Link to="/doors" className="bg-white p-6 text-center hover:shadow-lg transition-shadow border border-[#E5E5E5] hover:border-[#FF6B00]">
              <h3 className="font-bold text-[#0A0A0A] mb-2">Composite & uPVC Doors</h3>
              <p className="text-sm text-[#525252]">Secure front doors & patio doors</p>
            </Link>
            <Link to="/outbuilds" className="bg-white p-6 text-center hover:shadow-lg transition-shadow border border-[#E5E5E5] hover:border-[#FF6B00]">
              <h3 className="font-bold text-[#0A0A0A] mb-2">Garden Rooms</h3>
              <p className="text-sm text-[#525252]">Home offices & extensions</p>
            </Link>
            <Link to="/faq" className="bg-white p-6 text-center hover:shadow-lg transition-shadow border border-[#E5E5E5] hover:border-[#FF6B00]">
              <h3 className="font-bold text-[#0A0A0A] mb-2">Window Installation FAQ</h3>
              <p className="text-sm text-[#525252]">Motherwell, Glasgow & Lanarkshire</p>
            </Link>
          </div>
          <div className="mt-8 text-center">
            <p className="text-[#525252] mb-4">
              See what our customers say about us:
            </p>
            <Link to="/reviews" className="text-[#FF6B00] font-bold uppercase text-sm tracking-wide hover:underline">
              Read Customer Reviews →
            </Link>
          </div>
        </div>
      </section>

      {/* About C.Glancey Double Glazing Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2
                className="text-2xl sm:text-3xl font-black text-[#0A0A0A] mb-6"
                style={{ fontFamily: "'Chivo', sans-serif" }}
              >
                ABOUT C.GLANCEY <span className="text-[#FF6B00]">DOUBLE GLAZING</span>
              </h2>
              <p className="text-[#525252] mb-4">
                C.Glancey Double Glazing has been serving homeowners across Lanarkshire, Glasgow, and the surrounding areas for years, 
                building a reputation for quality workmanship, honest pricing, and exceptional customer service. We specialise in the 
                supply and installation of premium double glazing windows, composite doors, uPVC doors, and bespoke garden rooms.
              </p>
              <p className="text-[#525252] mb-4">
                Our team of experienced fitters take pride in every installation, ensuring that each project is completed to the highest 
                standards. From initial consultation to final inspection, we work closely with our customers to understand their needs 
                and deliver solutions that exceed expectations. Whether you need a single window replaced or a complete home transformation, 
                we have the expertise and products to help.
              </p>
              <p className="text-[#525252] mb-4">
                We only use the finest materials from trusted manufacturers like Rehau and Veka, ensuring that your new windows and doors 
                will perform beautifully for years to come. All our products are backed by a comprehensive 10-year guarantee, giving you 
                complete peace of mind with your investment.
              </p>
              <p className="text-[#525252]">
                As a local family business, we understand the importance of treating every customer's home with respect. Our fitters 
                are professional, punctual, and always leave properties clean and tidy. We believe in building long-term relationships 
                with our customers, which is why so many of our new clients come from personal recommendations.
              </p>
            </div>
            <div>
              <h3
                className="text-xl font-black text-[#0A0A0A] mb-4"
                style={{ fontFamily: "'Chivo', sans-serif" }}
              >
                WHY CHOOSE US FOR DOUBLE GLAZING IN LANARKSHIRE?
              </h3>
              <ul className="space-y-3 mb-6">
                {[
                  "Family-run business with local knowledge and expertise",
                  "A-rated energy efficient windows to reduce your heating bills",
                  "Premium Rehau and Veka profiles for lasting quality",
                  "Multi-point locking systems for enhanced security",
                  "Wide range of styles, colours, and finishes available",
                  "Professional installation by experienced craftsmen",
                  "Comprehensive 10-year guarantee on all products",
                  "Free no-obligation quotes with honest pricing",
                  "Fully insured and all work guaranteed",
                  "Serving Motherwell, Hamilton, Glasgow and all of Lanarkshire"
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-[#FF6B00] mt-0.5 flex-shrink-0" />
                    <span className="text-[#525252]">{item}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/contact"
                className="inline-block bg-[#FF6B00] text-white font-bold uppercase tracking-wide px-8 py-4 hover:bg-[#E65000] transition-all"
              >
                Get Your Free Quote Today
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Areas We Cover Section */}
      <section className="py-16 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2
            className="text-2xl sm:text-3xl font-black text-white mb-8 text-center"
            style={{ fontFamily: "'Chivo', sans-serif" }}
          >
            DOUBLE GLAZING ACROSS <span className="text-[#FF6B00]">LANARKSHIRE & GLASGOW</span>
          </h2>
          <p className="text-gray-300 text-center max-w-3xl mx-auto mb-8">
            We provide double glazing installation services throughout Lanarkshire, Glasgow, and the surrounding areas. 
            Our local knowledge means we understand the specific requirements of homes in this region, from Victorian 
            tenements in Glasgow to modern builds in East Kilbride.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4 mb-8">
            {[
              { name: "Hamilton", link: "/double-glazing-hamilton" },
              { name: "Motherwell", link: "/double-glazing-motherwell" },
              { name: "East Kilbride", link: "/double-glazing-east-kilbride" },
              { name: "Airdrie", link: "/double-glazing-airdrie" },
              { name: "Coatbridge", link: "/double-glazing-coatbridge" },
              { name: "Wishaw", link: "/double-glazing-wishaw" },
            ].map((area) => (
              <Link 
                key={area.name}
                to={area.link}
                className="bg-[#1A1A1A] p-4 text-center hover:bg-[#FF6B00] transition-colors group"
              >
                <span className="text-white font-medium group-hover:text-white">{area.name}</span>
              </Link>
            ))}
          </div>
          <p className="text-gray-400 text-center text-sm">
            Also covering: Bellshill, Uddingston, Blantyre, Larkhall, Carluke, Newton Mearns, Giffnock, Rutherglen, 
            Cambuslang, Paisley, Clydebank, and all surrounding areas. Contact us to check if we cover your area.
          </p>
        </div>
      </section>

      {/* Trusted Trader Badge */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <img
            src="https://customer-assets.emergentagent.com/job_6410c0e7-0424-4adb-b238-abc791a4a948/artifacts/q8rvdl7j_trusted_trader_lanarkshire_logo.png"
            alt="Trusted Trader Lanarkshire"
            data-testid="trusted-trader-badge"
            className="mx-auto h-40 w-auto"
          />
        </div>
      </section>
    </div>
  );
};

export default HomePage;
